import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Slider,
  Divider,
  InputAdornment,
} from '@mui/material';
import { PoolSettings, PoolSettingsUpdate } from '../../types';

interface WaterQualitySettingsFormProps {
  settings: PoolSettings['water_quality_thresholds'];
  onChange: (data: PoolSettingsUpdate['water_quality_thresholds']) => void;
}

const WaterQualitySettingsForm: React.FC<WaterQualitySettingsFormProps> = ({
  settings,
  onChange,
}) => {
  const [formData, setFormData] = useState({
    ph: { min: settings.ph.min, max: settings.ph.max },
    chlorine: { min: settings.chlorine.min, max: settings.chlorine.max },
    temperature: { min: settings.temperature.min, max: settings.temperature.max },
    turbidity: settings.turbidity
      ? { min: settings.turbidity.min, max: settings.turbidity.max }
      : { min: 0, max: 5 },
  });

  // settings prop이 변경되면 formData 업데이트
  useEffect(() => {
    setFormData({
      ph: { min: settings.ph.min, max: settings.ph.max },
      chlorine: { min: settings.chlorine.min, max: settings.chlorine.max },
      temperature: { min: settings.temperature.min, max: settings.temperature.max },
      turbidity: settings.turbidity
        ? { min: settings.turbidity.min, max: settings.turbidity.max }
        : { min: 0, max: 5 },
    });
  }, [settings]);

  // 입력값 변경 시 formData 업데이트 및 상위 컴포넌트에 전달
  const handleInputChange = (
    field: 'ph' | 'chlorine' | 'temperature' | 'turbidity',
    minMax: 'min' | 'max',
    value: number
  ) => {
    // 최소값이 최대값보다 크지 않도록 검증
    if (minMax === 'min' && value > formData[field].max) {
      value = formData[field].max;
    }
    // 최대값이 최소값보다 작지 않도록 검증
    if (minMax === 'max' && value < formData[field].min) {
      value = formData[field].min;
    }

    const newFormData = {
      ...formData,
      [field]: {
        ...formData[field],
        [minMax]: value,
      },
    };

    setFormData(newFormData);
    onChange(newFormData);
  };

  // pH 슬라이더 값 변경
  const handlePhSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [min, max] = newValue;
      const newFormData = {
        ...formData,
        ph: { min, max },
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 염소 슬라이더 값 변경
  const handleChlorineSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [min, max] = newValue;
      const newFormData = {
        ...formData,
        chlorine: { min, max },
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 온도 슬라이더 값 변경
  const handleTemperatureSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [min, max] = newValue;
      const newFormData = {
        ...formData,
        temperature: { min, max },
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  // 탁도 슬라이더 값 변경
  const handleTurbiditySliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [min, max] = newValue;
      const newFormData = {
        ...formData,
        turbidity: { min, max },
      };
      setFormData(newFormData);
      onChange(newFormData);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        수질 관리 기준 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        시설의 안전한 수질 상태를 유지하기 위한 기준값을 설정. 각 항목별 허용 범위를 지정하면
        해당 범위를 벗어날 경우 경고가 발생
      </Typography>

      <Grid container spacing={3}>
        {/* pH 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="pH 기준" 
              subheader="산성/알칼리성 농도 기준"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="최소값"
                      type="number"
                      value={formData.ph.min}
                      onChange={(e) => handleInputChange('ph', 'min', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 14, step: 0.1 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="최대값"
                      type="number"
                      value={formData.ph.max}
                      onChange={(e) => handleInputChange('ph', 'max', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 14, step: 0.1 },
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4, mx: 1 }}>
                  <Slider
                    value={[formData.ph.min, formData.ph.max]}
                    onChange={handlePhSliderChange}
                    min={0}
                    max={14}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 7, label: '7' },
                      { value: 14, label: '14' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">산성</Typography>
                    <Typography variant="caption" color="text.secondary">중성</Typography>
                    <Typography variant="caption" color="text.secondary">알칼리성</Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  권장 범위: 7.2 ~ 7.8 (대부분의 공공 시설 기준)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 염소 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="염소 기준" 
              subheader="소독 약품 농도 기준"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="최소값"
                      type="number"
                      value={formData.chlorine.min}
                      onChange={(e) => handleInputChange('chlorine', 'min', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 10, step: 0.1 },
                        endAdornment: <InputAdornment position="end">ppm</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="최대값"
                      type="number"
                      value={formData.chlorine.max}
                      onChange={(e) => handleInputChange('chlorine', 'max', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 10, step: 0.1 },
                        endAdornment: <InputAdornment position="end">ppm</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4, mx: 1 }}>
                  <Slider
                    value={[formData.chlorine.min, formData.chlorine.max]}
                    onChange={handleChlorineSliderChange}
                    min={0}
                    max={10}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  권장 범위: 1.0 ~ 3.0 ppm (일반 시설 기준)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 수온 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="수온 기준" 
              subheader="적정 수온 범위 설정"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="최소값"
                      type="number"
                      value={formData.temperature.min}
                      onChange={(e) => handleInputChange('temperature', 'min', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 10, max: 40, step: 0.5 },
                        endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="최대값"
                      type="number"
                      value={formData.temperature.max}
                      onChange={(e) => handleInputChange('temperature', 'max', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 10, max: 40, step: 0.5 },
                        endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4, mx: 1 }}>
                  <Slider
                    value={[formData.temperature.min, formData.temperature.max]}
                    onChange={handleTemperatureSliderChange}
                    min={10}
                    max={40}
                    step={0.5}
                    marks={[
                      { value: 10, label: '10°C' },
                      { value: 25, label: '25°C' },
                      { value: 40, label: '40°C' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  권장 범위: 25 ~ 28°C (경기용 25-28°C, 휴양용 28-30°C)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 탁도 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="탁도 기준" 
              subheader="시설 물의 투명도 기준"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <CardContent>
              <Box sx={{ px: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="최소값"
                      type="number"
                      value={formData.turbidity.min}
                      onChange={(e) => handleInputChange('turbidity', 'min', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 10, step: 0.1 },
                        endAdornment: <InputAdornment position="end">NTU</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="최대값"
                      type="number"
                      value={formData.turbidity.max}
                      onChange={(e) => handleInputChange('turbidity', 'max', parseFloat(e.target.value))}
                      fullWidth
                      InputProps={{
                        inputProps: { min: 0, max: 10, step: 0.1 },
                        endAdornment: <InputAdornment position="end">NTU</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4, mx: 1 }}>
                  <Slider
                    value={[formData.turbidity.min, formData.turbidity.max]}
                    onChange={handleTurbiditySliderChange}
                    min={0}
                    max={10}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  권장 범위: 0.5 이하 (시설 물이 맑을수록 낮은 값)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WaterQualitySettingsForm; 